/**
 * Levels: how far through the words of each difficulty the child has got.
 *
 * **The level is the difficulty band**, which already exists and already
 * decides which words the app hands out. Nothing was invented to make a game
 * of it.
 *
 * **Cleared by GROWING words, never by answering questions.** Growing a word
 * means reaching the floor in all three bags — twelve correct answers across
 * twelve separate sittings, because only one per bag counts per session. It
 * is the one thing in the app that cannot be rushed, which is what makes it
 * safe to build a game on. A level cleared by "questions answered" could be
 * farmed in an afternoon (D-049).
 *
 * **Nothing is locked.** Every level is visible and every word stays
 * available whatever this screen says. A level is a map of where the child
 * has got to, not a gate. Locking content behind progress is the Prodigy
 * mechanic and the competitor research is more damning about it than about
 * anything else.
 *
 * **Nothing falls.** The bar measures words grown against a fixed target, not
 * a percentage of what has been met — a percentage drops every time a new word
 * arrives, which is the falling number D-048 exists to prevent. And a cleared
 * level stays cleared: the star is stamped once and never comes off.
 */
import { useEffect, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import { api, type Levels as LevelsData } from '../api'
import { radius, theme } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Trace, TraceRegion } from '../lib/trace'

export function Levels({
  learnerId, onHome, onGarden, onWordMap,
}: {
  learnerId: string; onHome: () => void
  onGarden: () => void; onWordMap: () => void
}) {
  const [data, setData] = useState<LevelsData | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = () => {
    setError(null)
    api.levels(learnerId).then(setData).catch(setError)
  }
  useEffect(load, [learnerId])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data) return <Loading what="Counting up" />

  return (
    <Page width={620}>
      <Heading sub={
        <>
          <Trace id="calc.levels where grown reached the target, stamped in T20 setting">
            {data.cleared_count} cleared
          </Trace>
          {' · '}
          <Trace id="calc.rows in T7 learner_word with grown_at set"
                 title="a stamp that is never cleared: this cannot go down">
            {data.total_grown} words fully grown
          </Trace>
        </>
      }>
        Your levels
      </Heading>

      {data.empty_message ? (
        <Card style={{ textAlign: 'center', color: theme.muted, padding: 30 }}>
          {data.empty_message}
        </Card>
      ) : (
        <TraceRegion id="T4" note="grouped by difficulty_band, counted from T7">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.levels.filter(l => l.met > 0).map((level) => {
            const here = level.band === data.working_on
            const done = Math.min(1, level.grown / Math.max(level.target, 1))
            return (
              <Card
                key={level.band}
                style={{
                  padding: 16,
                  borderColor: level.cleared ? theme.green
                    : here ? theme.orange : theme.borderWarm,
                  borderWidth: here || level.cleared ? 2 : 1,
                  background: level.cleared ? theme.greenPale
                    : here ? theme.orangePale : theme.card,
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginBottom: 10, flexWrap: 'wrap',
                }}>
                  <span style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 800,
                    color: level.cleared ? '#FFFFFF' : theme.text2,
                    background: level.cleared ? theme.green : theme.cardWarm,
                    border: `2px solid ${level.cleared ? theme.green : theme.border}`,
                  }}>
                    {level.cleared ? '★' : level.band}
                  </span>

                  <span style={{
                    fontSize: 17, fontWeight: 800, flex: 1, minWidth: 120,
                    color: level.cleared ? theme.greenDarker : theme.text,
                  }}>
                    {level.name}
                  </span>

                  {here ? (
                    <span style={{
                      fontSize: 12, fontWeight: 800, letterSpacing: 0.6,
                      color: theme.orangeDark, background: theme.orangeTint,
                      borderRadius: radius.pill, padding: '3px 10px',
                    }}>
                      YOU ARE HERE
                    </span>
                  ) : null}

                  <span style={{ fontSize: 14, color: theme.muted }}>
                    <Trace id="calc.words at this band with grown_at set">
                      {level.grown}
                    </Trace>
                    {' of '}{level.target}
                  </span>
                </div>

                {/* Grown against a fixed target, so it fills and never
                    empties. A percentage of words met would drop every time a
                    new word arrived at this band. */}
                <div style={{
                  height: 12, borderRadius: radius.pill, overflow: 'hidden',
                  background: theme.cardWarm,
                  border: `1px solid ${theme.borderSoft}`,
                }}>
                  <div style={{
                    width: `${done * 100}%`, height: '100%',
                    background: level.cleared ? theme.green : theme.orange,
                    borderRadius: radius.pill,
                    transition: 'width 400ms ease',
                  }} />
                </div>

                <div style={{ fontSize: 13, color: theme.faint, marginTop: 7 }}>
                  {level.cleared
                    ? `Cleared. ${level.met} words met at this level.`
                    : `${Math.max(0, level.target - level.grown)} more to clear this level`}
                </div>
              </Card>
            )
          })}
        </div>
        </TraceRegion>
      )}

      <p style={{
        fontSize: 13, color: theme.faint, lineHeight: 1.55, margin: '16px 2px 0',
      }}>
        A word counts once you have got it right on twelve different days —
        recognising it, spelling it, and using it. Nothing here can go down,
        and nothing is locked.
      </p>

      <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
        <Button tone="quiet" onClick={onWordMap}>🗺️ Word map</Button>
        <Button tone="quiet" onClick={onGarden}>🌿 Garden</Button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}
